import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { Navigation, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import { Users, MapPin, MessageSquare, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import OptimizedImage from './OptimizedImage'
import { groupService } from '../lib/groupService'
import type { Group } from '../types/group'



const calculateTrend = (group: Group): 'alta' | 'crescendo' | 'estável' => {
  // Heurística baseada em memberCount, postCount e atividade recente
  const now = new Date()
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  const isRecentActivity = group.recentActivity > sevenDaysAgo
  
  // Pontuação simples
  let score = 0
  if (group.memberCount > 500) score += 2
  else if (group.memberCount > 200) score += 1
  
  if (group.postCount > 100) score += 2
  else if (group.postCount > 50) score += 1
  
  if (isRecentActivity) score += 1
  
  if (score >= 4) return 'alta'
  if (score >= 2) return 'crescendo'
  return 'estável'
}

const TrendingGroups = () => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadGroups = async () => {
      try {
        // Buscar grupos reais, ordenados por atividade recente
        const fetchedGroups = await groupService.getGroups({ sortBy: 'active' })
        // Pegar os primeiros 8 grupos mais ativos
        setGroups(fetchedGroups.slice(0, 8))
      } catch (error) {
        console.error('Erro ao carregar grupos:', error)
      } finally {
        setLoading(false)
      }
    }
    loadGroups()
  }, [])

  return (
    <div className="w-full py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
          <div>
             <h2 className="text-3xl md:text-4xl font-bold text-noe-100 mb-2">
              Grupos em <span className="text-noe-brand">Tendência</span>
            </h2>
            <p className="text-lg text-noe-300">
              Descubra comunidades ativas que estão bombando agora
            </p>
          </div>
          
           <div className="flex items-center gap-4 mt-4 md:mt-0">
             <button
               className="p-3 rounded-full bg-noe-800/50 hover:bg-noe-700/50 transition-colors disabled:opacity-30 border-none"
               onClick={() => swiperInstance?.slidePrev()}
               aria-label="Slide anterior"
             >
                <ChevronLeft className="w-5 h-5 text-noe-100" />
             </button>
             <button
               className="p-3 rounded-full bg-noe-800/50 hover:bg-noe-700/50 transition-colors disabled:opacity-30 border-none"
               onClick={() => swiperInstance?.slideNext()}
               aria-label="Próximo slide"
             >
                <ChevronRight className="w-5 h-5 text-noe-100" />
             </button>
            <Link
              to="/groups"
              className="px-6 py-3 bg-noe-brand text-noe-950 font-medium rounded-lg hover:bg-noe-brand/90 transition-colors"
            >
              Ver todos os grupos
            </Link>
          </div>
        </div>

         {loading ? (
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pb-12">
             {[1, 2, 3, 4].map((i) => (
               <div key={i} className="bg-noe-800/30 border border-noe-700/50 rounded-2xl overflow-hidden h-full">
                 <div className="h-48 bg-noe-800/50 animate-pulse"></div>
                 <div className="p-5">
                   <div className="h-6 bg-noe-800/50 rounded animate-pulse mb-2"></div>
                   <div className="h-4 bg-noe-800/50 rounded animate-pulse mb-4"></div>
                   <div className="flex justify-between">
                     <div className="h-4 bg-noe-800/50 rounded animate-pulse w-20"></div>
                     <div className="h-4 bg-noe-800/50 rounded animate-pulse w-16"></div>
                   </div>
                 </div>
               </div>
             ))}
           </div>
         ) : (
           <Swiper
             onSwiper={setSwiperInstance}
             modules={[Navigation, Autoplay]}
             slidesPerView={1}
             spaceBetween={20}
             autoplay={{ delay: 4000, disableOnInteraction: false }}
             breakpoints={{
               640: { slidesPerView: 2 },
               1024: { slidesPerView: 3 },
               1280: { slidesPerView: 4 },
             }}
             className="pb-12"
           >
             {groups.map((group) => {
               const trend = calculateTrend(group)
               return (
                 <SwiperSlide key={group.id}>
                   <Link
                     to={`/groups/${group.id}`}
                     className="block group bg-noe-800/30 border border-noe-700/50 rounded-2xl overflow-hidden hover:border-noe-brand/50 hover:bg-noe-800/50 transition-all duration-300 h-full"
                   >
                      <div className="relative h-48 overflow-hidden">
                        <OptimizedImage
                          src={group.coverImage || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                          alt={group.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          width={800}
                          height={300}
                        />
                        <div className="absolute top-4 right-4">
                          <div className={`px-3 py-1 rounded-full text-xs font-bold ${trend === 'alta' ? 'bg-green-500 text-green-900' : trend === 'crescendo' ? 'bg-blue-500 text-blue-900' : 'bg-yellow-500 text-yellow-900'}`}>
                            <TrendingUp className="w-3 h-3 inline mr-1" />
                            {trend}
                          </div>
                        </div>
                      </div>
                      
                       <div className="p-5">
                         <div className="mb-4">
                           <h3 className="text-xl font-bold text-noe-100">{group.name}</h3>
                           <p className="text-noe-300 text-sm">{group.category}</p>
                         </div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2 text-noe-300">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm">Nacional</span>
                          </div>
                          <div className="flex items-center gap-2 text-noe-300">
                            <MessageSquare className="w-4 h-4" />
                            <span className="text-sm">{group.postCount} posts</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-noe-brand" />
                            <span className="text-noe-100 font-semibold">{group.memberCount.toLocaleString('pt-BR')} membros</span>
                          </div>
                          <div className="px-4 py-1.5 bg-noe-brand/10 text-noe-brand rounded-full text-sm font-medium">
                            Participar
                          </div>
                        </div>
                      </div>
                   </Link>
                 </SwiperSlide>
               )
             })}
           </Swiper>
         )}
        
        <div className="text-center mt-10 pt-8 border-t border-noe-800/50">
          <p className="text-noe-400 text-sm md:text-base">
            Esses grupos são selecionados com base em atividade, crescimento e engajamento recente.
          </p>
          <p className="text-noe-brand font-medium mt-2">
            Participe das discussões e expanda sua rede!
          </p>
        </div>
      </div>
    </div>
  )
}

export default TrendingGroups